// Backlog 271 — pure renderer for `cloudgrid logs --deploy <id>`.
//
// Input:  the deploy trace returned by `GET /deploys/<id>`.
// Output: a multi-line string suitable for stdout + a boolean telling
//         the caller whether a structured `build_log_excerpt` was found.
//
// The §23 voice rules apply (plain, specific, no emoji, no exclamation
// marks). Output uses a small box drawing to mirror the existing
// `cloudgrid plug` build-log surface (see `services/build-log-tail.ts`).

import type { DeployTrace } from './plug-poll.js';

export interface RenderedBuildLog {
  /** Ready-for-stdout body. Always ends with a newline. */
  body: string;
  /**
   * True when the trace carried a `error.build_log_excerpt` with a
   * non-empty `text`. False when we fell back to printing only the
   * build_id / console URL (or nothing at all). The caller uses this to
   * decide the process exit code.
   */
  hasExcerpt: boolean;
}

/**
 * Render `error.build_log_excerpt` from a deploy trace into a stdout
 * body. Falls back gracefully across the three observable shapes:
 *
 *   1. Full excerpt present (text + build_id + console_url) — print
 *      a boxed tail with a header naming the build id and a footer
 *      pointing at the console URL.
 *   2. Build id only (server-side fetch failed but the trace still
 *      carries an id) — print the headline + console URL hint.
 *   3. Nothing structured at all (legacy event, non-build failure) —
 *      print a §23-voiced explanation pointing at `cloudgrid follow`.
 */
export function renderDeployBuildLogExcerpt(
  trace: DeployTrace,
): RenderedBuildLog {
  const traceId = trace.trace_id;
  const entityName = trace.entity?.name ?? '<unknown>';
  const errorBody = trace.error as
    | {
        code?: string;
        message?: string;
        message_user?: string;
        failed_step?: string;
        build_log_excerpt?: {
          text?: string;
          build_id?: string;
          console_url?: string;
          service?: string;
        };
      }
    | null;

  const head: string[] = [];
  head.push(`Deploy ${traceId}  ·  entity: ${entityName}  ·  status: ${trace.status}`);
  if (errorBody) {
    const codeLine = errorBody.code ?? 'failure';
    const stepLine = errorBody.failed_step ?? '<unknown>';
    head.push(`  code: ${codeLine}  ·  step: ${stepLine}`);
    if (typeof errorBody.message_user === 'string' && errorBody.message_user.length > 0) {
      head.push(`  ${errorBody.message_user}`);
    } else if (typeof errorBody.message === 'string' && errorBody.message.length > 0) {
      head.push(`  ${errorBody.message}`);
    }
  }

  const excerpt = errorBody?.build_log_excerpt;
  if (excerpt && typeof excerpt.text === 'string' && excerpt.text.length > 0) {
    const lines = excerpt.text.split('\n');
    const idShort = excerpt.build_id ? excerpt.build_id.slice(0, 8) : 'unknown';
    const headerLine = `  ┌─ build log tail (${lines.length} lines, build_id ${idShort}...) ─`;
    const body: string[] = [...head, '', headerLine];
    for (const line of lines) body.push(`  │ ${line}`);
    if (excerpt.console_url) {
      body.push(`  └─ full log: ${excerpt.console_url}`);
    } else if (excerpt.build_id) {
      body.push(
        `  └─ full log: gcloud builds log ${excerpt.build_id} --project=dev-atomic`,
      );
    } else {
      body.push('  └─');
    }
    return { body: body.join('\n') + '\n', hasExcerpt: true };
  }

  // No structured excerpt. If we have a build id we can still hand the
  // user a way forward; otherwise fall through to a §23-voiced miss.
  const body: string[] = [...head, ''];
  if (excerpt && excerpt.build_id) {
    body.push(
      `  No log tail was captured for this deploy, but the build id is known:`,
    );
    if (excerpt.console_url) {
      body.push(`    full log: ${excerpt.console_url}`);
    } else {
      body.push(`    full log: gcloud builds log ${excerpt.build_id} --project=dev-atomic`);
    }
  } else {
    body.push(
      '  No captured build log excerpt for this deploy.',
      `  See the full deploy trace: cloudgrid follow ${traceId}`,
    );
  }
  return { body: body.join('\n') + '\n', hasExcerpt: false };
}
